<template>
  <PageColumns class="post-review">
    <PageContent :heading="heading">
      <template v-if="notFound">
        <p>The event is unavailable or may have been deleted.</p>
      </template>
      <template v-else-if="submitted">
        <SuccessMessage>
          <p>Your review was saved successfully.</p>
        </SuccessMessage>
        <div class="flex-split wrap mt-16">
          <IconLink icon="arrow-left" class="mt-8 mb-8" style="margin-left: auto;" :to="{ name: $routes.Dashboard.name }"
            >Return to Dashboard</IconLink
          >
        </div>
      </template>
      <ReviewForm
        v-else
        v-model="review"
        :reviewee="revieweeProfile"
        :verb="verb"
        ref="form"
        @save="onSave"
      />
    </PageContent>
    <PageSidebar accent aria-label="Subject">
      <div class="sticky" v-if="!notFound">
        <ProfileCard
          v-model="revieweeProfile"
          class="post-review-reviewee"
          :include="null"
          :link="false"
          portrait
        >
          <p v-if="role">
            {{ role.name }}
            <template v-if="event && event.name"> <i>at</i> {{ event.name }}</template>
          </p>
          <p v-if="eventDateISOString && eventDateString">
            <time :datetime="eventDateISOString">
              {{ eventDateString }}
            </time>
          </p>
        </ProfileCard>
        <div class="post-review-actions actions" v-if="!submitted">
          <BaseButton class="button-primary button-full button-large" @click="submit">
            {{ verb }}
          </BaseButton>
        </div>
      </div>
    </PageSidebar>
  </PageColumns>
</template>

<script>
import { DateTime } from 'luxon';
import { toGuid } from '@/helpers/convert';
import Review from '@/models/Review';
import Role from '@/models/Role';

import ReviewForm from '@/components/forms/ReviewForm';

export default {
  name: 'ReviewsCreate',
  components: { ReviewForm },
  data() {
    return {
      /** @type {JobEvent | null} */
      event: null,
      /** @type {boolean} */
      isLoading: false,
      /** @type {Job | null} */
      job: null,
      /** @type {Review | null} */
      review: new Review(),
      /** @type {Profile | null} */
      revieweeProfile: null,
      /** @type {boolean} */
      submitted: false,
    };
  },
  computed: {
    /**
     * @returns {import('luxon').DateTime | null}
     */
    eventDate() {
      return this.$toDateTime(this.event?.startedAt, { zone: this.eventTimeZone });
    },
    /**
     * @returns {boolean}
     */
    eventDateHasTime() {
      const date = this.eventDate;
      return !!date && (date.hour !== 0 || date.minute !== 0 || date.second !== 1);
    },
    /**
     * @returns {string | null}
     */
    eventDateISOString() {
      const date = this.eventDate;
      if (!date) {
        return null;
      }
      return this.eventDateHasTime ? date.toISO() : date.toISODate();
    },
    /**
     * @returns {string | null}
     */
    eventDateString() {
      const date = this.eventDate;
      return date ? date.toLocaleString(DateTime.DATE_FULL) : null;
    },
    /**
     * @returns {import('luxon').Zone}
     */
    eventTimeZone() {
      return this.event?.timeZone || this.$localTimeZone;
    },
    /**
     * @returns {string}
     */
    heading() {
      const name = this.revieweeProfile?.name;
      return `Review${name ? ` for ${name}` : ''}`;
    },
    /**
     * @returns {string | null}
     */
    jobContractId() {
      return toGuid(this.$route.query.jobContractId);
    },
    /**
     * @returns {boolean}
     */
    notFound() {
      if (this.isLoading) {
        return false;
      }
      /** @type {JobEvent | null} */
      let event;
      /** @type {JobEvent | null} */
      let job;
      /** @type {Profile | null} */
      let revieweeProfile;
      return (
        !(job = this.job) ||
        job.isDeleted ||
        !(event = this.event) ||
        event.isDeleted ||
        !(revieweeProfile = this.revieweeProfile) ||
        revieweeProfile.isDeleted ||
        revieweeProfile.userId === this.$activeUser?.id
      );
    },
    /**
     * @returns {Role | null}
     */
    role() {
      const job = this.job;
      if (!job || !this.$store.getters['roles/list']) {
        return;
      }
      return Role.from(job.positionId);
    },
    /**
     * @returns {string}
     */
    verb() {
      const reviewId = this.review?.id;
      return `${reviewId ? 'Save' : 'Post'} Review`;
    },
  },
  methods: {
    /**
     * @returns {void}
     */
    clear() {
      this.event = null;
      this.job = null;
      this.revieweeProfile = null;
    },
    /**
     * @returns {Promise<void>}
     */
    async load() {
      const jobContractId = this.jobContractId;
      if (!jobContractId) {
        this.clear();
        return;
      }

      const canceled = (() => {
        let wasCanceled = false;
        return () => {
          if (wasCanceled) {
            return true;
          }
          return wasCanceled || (wasCanceled = this.jobContractId !== jobContractId);
        };
      })();
      const dispatch = this.$store.dispatch.bind(this.$store);

      this.clear();
      this.isLoading = true;

      try {
        /** @type {User | null} */
        const activeUser = await dispatch('users/getActiveUser');
        const activeUserId = activeUser.id;
        if (!activeUser || !activeUserId) {
          this.$router.replace({ name: this.$routes.Login.name });
          return;
        }

        // 1. Load job contract to get job ID and reviewee ID.
        /** @type {JobContract} */
        const contract = await dispatch('jobs/getJobContract', jobContractId);
        const { jobId, employeeId } = contract;
        if (canceled() || !jobId || !employeeId) {
          return;
        }

        /** @type {JobEvent | Promise<JobEvent | null> | null | undefined} */
        let event;
        /** @type {Job | null | undefined} */
        let job;

        // 2. Load existing reviews for the job to figure out whether we're creating a new review or
        //    editing an existing one.
        /** @type {Review[]} */
        const reviews = await dispatch('reviews/getJobReviews', jobId);
        if (canceled()) return;

        const review = reviews.find((r) => r.jobContractId === jobContractId);
        if (review) {
          event = review.event;
          job = review.job;
        }

        // 3. Load job
        if (!job) {
          job = await dispatch('jobs/getJob', jobId);
          if (canceled() || !job || job.id !== jobId || job.isDeleted) return;
        }

        // 4. Get event from job.event, contract.event, job.eventId, or contract.eventId, whichever
        //    is available first
        if (!event && !(event = contract.event) && !(event = job.event)) {
          const eventId = job.eventId || contract.eventId;
          if (!eventId) {
            return;
          }
          // `await` is omitted so this request runs concurrently with others
          event = dispatch('events/getEvent', eventId);
        }

        // 5. Determine whether the `revieweeId` is the `creatorId` or the `employeeId`
        const creatorId = job.creatorId || (await event)?.creatorId;
        if (!creatorId) {
          return;
        }
        /** @type {UserId | undefined} */
        let revieweeId;
        if (creatorId === activeUserId) {
          if (employeeId === activeUserId) {
            return;
          }
          revieweeId = employeeId;
        } else {
          revieweeId = creatorId;
        }

        // 6. Get reviewee profile
        /** @type {Profile | Promise<Profile | null> | null | undefined} */
        let revieweeProfile = review?.reviewee?.profile;
        if (!revieweeProfile || revieweeProfile.userId !== revieweeId) {
          // `await` is omitted so this request runs concurrently with others
          revieweeProfile = dispatch('profiles/getProfile', { userId: revieweeId });
        }

        if (
          canceled() ||
          !(event = await event) ||
          event.isDeleted ||
          !(revieweeProfile = await revieweeProfile) ||
          revieweeProfile.isDeleted
        ) {
          return;
        }

        this.event = event;
        this.job = job;
        this.revieweeProfile = revieweeProfile;

        this.review =
          review ||
          new Review({
            event,
            eventId: event.id,
            revieweeId: revieweeId,
            reviewer: activeUser,
            reviewerId: activeUserId,
            job,
            jobContractId,
            jobId,
          });
      } finally {
        if (!canceled()) {
          this.isLoading = false;
        }
      }
    },
    /**
     * @returns {void}
     */
    onSave() {
      this.submitted = true;
      document.scrollingElement.scrollTop = 0;
    },
    /**
     * @returns {void}
     */
    submit() {
      this.$refs.form.requestSubmit();
    },
  },
  watch: {
    $route: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch('roles/getRoles');
  },
  metaInfo: {
    title: 'Leave a Review',
  },
};
</script>
